import Phaser from "phaser";
import {
  PUZZLE_BOARD_WIDTH,
  PUZZLE_SHOOTER_X,
  PUZZLE_SHOOTER_Y,
  type PuzzleEvent,
  type PuzzleMatchState
} from "../../simulation";
import type { PuzzleSceneBridge } from "../sceneBridge";
import { puzzleGuidePolicy } from "../guidePolicy";
import {
  FOAM_POP_ASSET_PATHS,
  FOAM_POP_BUBBLE_SHEET,
  FOAM_POP_MASCOT_FRAME,
  FOAM_POP_MASCOT_SHEET,
  FOAM_POP_TEXTURE_KEYS,
  FOAM_POP_WHEEL_FIRE_ANIMATION_KEY,
  FOAM_POP_WHEEL_MASCOT_FRAME,
  FOAM_POP_WHEEL_MASCOT_SHEET,
  foamPopWheelAimFrame,
  foamPopBubbleFrame
} from "../../assets/manifest";
import {
  normalizePuzzleBubbleKind,
  puzzleColorStyle,
  puzzleBubbleKindLabel,
  summarizePuzzleEvent,
  toPuzzleViewModel,
  type PuzzleBoardView,
  type PuzzleBubbleKindView,
  type PuzzleTeamView,
  type PuzzleViewModel
} from "../viewModel";

export const PUZZLE_RENDER_WIDTH = 1280;
export const PUZZLE_RENDER_HEIGHT = 840;
export const PUZZLE_PORTRAIT_RENDER_WIDTH = 720;
export const PUZZLE_PORTRAIT_RENDER_HEIGHT = 1260;

const LANDSCAPE_PRIMARY_SCALE = 1.025;
const LANDSCAPE_PRIMARY_Y = 30;
const DUEL_COMPARISON_SCALE = 0.98;
const DUEL_COMPARISON_GAP = 110;
const DUEL_COMPARISON_Y = 48;
const PORTRAIT_PRIMARY_SCALE = 1.44;
const PORTRAIT_PRIMARY_X = 8;
const PORTRAIT_PRIMARY_Y = 150;
const PORTRAIT_OPPONENT_SCALE = 0.58;
const PORTRAIT_OPPONENT_X = 514;
const PORTRAIT_OPPONENT_Y = 120;

export interface PuzzleAimGeometry {
  gameWidth: number;
  gameHeight: number;
  launcherX: number;
  launcherY: number;
}

export function puzzleAimGeometry(portrait = false, duelComparison = false): PuzzleAimGeometry {
  if (portrait) {
    return {
      gameWidth: PUZZLE_PORTRAIT_RENDER_WIDTH,
      gameHeight: PUZZLE_PORTRAIT_RENDER_HEIGHT,
      launcherX: PORTRAIT_PRIMARY_X
        + PUZZLE_SHOOTER_X * PORTRAIT_PRIMARY_SCALE,
      launcherY: PORTRAIT_PRIMARY_Y + PUZZLE_SHOOTER_Y * PORTRAIT_PRIMARY_SCALE
    };
  }
  if (duelComparison) {
    const boardWidth = PUZZLE_BOARD_WIDTH * DUEL_COMPARISON_SCALE;
    const startX = (PUZZLE_RENDER_WIDTH - boardWidth * 2 - DUEL_COMPARISON_GAP) / 2;
    return {
      gameWidth: PUZZLE_RENDER_WIDTH,
      gameHeight: PUZZLE_RENDER_HEIGHT,
      launcherX: startX + PUZZLE_SHOOTER_X * DUEL_COMPARISON_SCALE,
      launcherY: DUEL_COMPARISON_Y + PUZZLE_SHOOTER_Y * DUEL_COMPARISON_SCALE
    };
  }
  return {
    gameWidth: PUZZLE_RENDER_WIDTH,
    gameHeight: PUZZLE_RENDER_HEIGHT,
    launcherX: (PUZZLE_RENDER_WIDTH - PUZZLE_BOARD_WIDTH * LANDSCAPE_PRIMARY_SCALE) / 2
      + PUZZLE_SHOOTER_X * LANDSCAPE_PRIMARY_SCALE,
    launcherY: LANDSCAPE_PRIMARY_Y + PUZZLE_SHOOTER_Y * LANDSCAPE_PRIMARY_SCALE
  };
}

export interface PuzzleFocusRef {
  playerId?: string;
}

interface PuzzleSceneOptions {
  bridge: PuzzleSceneBridge;
  focus: PuzzleFocusRef;
  readAim: () => number;
  compact?: boolean;
  /** Portrait layout: primary board lower-left, comparisons upper-right. */
  portrait?: boolean;
  /** Local landscape 1:1 layout: equal-size player-left and AI-right boards. */
  duelComparison?: boolean;
}

interface BoardLayout {
  board: PuzzleBoardView;
  x: number;
  y: number;
  scale: number;
  primary: boolean;
}

const TEAM_STYLE: Readonly<Record<PuzzleTeamView, { accent: number; pale: number; shadow: number }>> = {
  blue: { accent: 0x36dbe5, pale: 0xc6fcff, shadow: 0x07566b },
  orange: { accent: 0xff806f, pale: 0xffd9cb, shadow: 0x813a43 }
};

function rawRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? value as Record<string, unknown> : {};
}

function compactPlayerName(value: string, limit: number): string {
  const normalized = value.trim() || "PLAYER";
  return normalized.length > limit ? `${normalized.slice(0, Math.max(1, limit - 1))}…` : normalized;
}

export class PuzzleScene extends Phaser.Scene {
  private readonly bridge: PuzzleSceneBridge;
  private readonly focus: PuzzleFocusRef;
  private readonly readAim: () => number;
  private readonly compact: boolean;
  private readonly portrait: boolean;
  private readonly duelComparison: boolean;
  private readonly renderWidth: number;
  private readonly renderHeight: number;
  private unsubscribeSnapshot: (() => void) | null = null;
  private unsubscribeEvents: (() => void) | null = null;
  private boardLayer: Phaser.GameObjects.Graphics | null = null;
  private guideLayer: Phaser.GameObjects.Graphics | null = null;
  private launcherLayer: Phaser.GameObjects.Graphics | null = null;
  private atmosphere: Phaser.GameObjects.Graphics | null = null;
  private view: PuzzleViewModel | null = null;
  private layouts = new Map<string, BoardLayout>();
  private labels = new Map<string, Phaser.GameObjects.Text>();
  private landingLabel: Phaser.GameObjects.Text | null = null;
  private comparisonBadge: Phaser.GameObjects.Text | null = null;
  private generatedBackground: Phaser.GameObjects.Image | null = null;
  private mascot: Phaser.GameObjects.Sprite | null = null;
  private mascotOverrideFrame: number | null = null;
  private mascotOverrideUntil = 0;
  private bubbleImages = new Map<string, Phaser.GameObjects.Image>();
  private visibleBubbleImages = new Set<string>();
  private lastGuideAim = Number.NaN;
  private reducedMotion = false;

  constructor(options: PuzzleSceneOptions) {
    super({
      key: options.compact
        ? "foam-pop-compact"
        : options.portrait
          ? "foam-pop-player-portrait"
          : options.duelComparison
            ? "foam-pop-player-duel-comparison"
            : "foam-pop-player"
    });
    this.bridge = options.bridge;
    this.focus = options.focus;
    this.readAim = options.readAim;
    this.compact = options.compact ?? false;
    this.portrait = !this.compact && (options.portrait ?? false);
    this.duelComparison = !this.compact && !this.portrait && (options.duelComparison ?? false);
    this.renderWidth = this.portrait ? PUZZLE_PORTRAIT_RENDER_WIDTH : PUZZLE_RENDER_WIDTH;
    this.renderHeight = this.portrait ? PUZZLE_PORTRAIT_RENDER_HEIGHT : PUZZLE_RENDER_HEIGHT;
  }

  preload(): void {
    this.load.spritesheet(FOAM_POP_TEXTURE_KEYS.bubbles, FOAM_POP_ASSET_PATHS.bubbles, {
      frameWidth: FOAM_POP_BUBBLE_SHEET.frameWidth,
      frameHeight: FOAM_POP_BUBBLE_SHEET.frameHeight
    });
    if (this.compact) return;
    this.load.image(
      this.portrait ? FOAM_POP_TEXTURE_KEYS.backgroundPortrait : FOAM_POP_TEXTURE_KEYS.backgroundLandscape,
      this.portrait ? FOAM_POP_ASSET_PATHS.backgroundPortrait : FOAM_POP_ASSET_PATHS.backgroundLandscape
    );
    this.load.spritesheet(FOAM_POP_TEXTURE_KEYS.mascot, FOAM_POP_ASSET_PATHS.mascot, {
      frameWidth: FOAM_POP_MASCOT_SHEET.frameWidth,
      frameHeight: FOAM_POP_MASCOT_SHEET.frameHeight
    });
    this.load.spritesheet(FOAM_POP_TEXTURE_KEYS.mascotWheel, FOAM_POP_ASSET_PATHS.mascotWheel, {
      frameWidth: FOAM_POP_WHEEL_MASCOT_SHEET.frameWidth,
      frameHeight: FOAM_POP_WHEEL_MASCOT_SHEET.frameHeight
    });
  }

  create(): void {
    this.reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    this.cameras.main.setBackgroundColor(0x030a12);
    this.applyPixelArtTextureFilters();
    this.createMascotAnimations();
    this.drawLabChamber();
    this.drawGeneratedBackground();
    this.boardLayer = this.add.graphics().setDepth(0);
    this.guideLayer = this.add.graphics().setDepth(3);
    this.launcherLayer = this.add.graphics().setDepth(5);
    this.landingLabel = this.add.text(0, 0, "LAND", {
      fontFamily: "Arial Narrow, Avenir Next Condensed, sans-serif",
      fontSize: this.compact ? "9px" : "11px",
      color: "#d9ffff",
      fontStyle: "bold"
    }).setOrigin(0.5, 1).setAlpha(this.compact ? 0.48 : 0.72).setDepth(8).setVisible(false);
    this.unsubscribeSnapshot = this.bridge.onSnapshot((state) => this.receiveSnapshot(state));
    this.unsubscribeEvents = this.bridge.onEvents((events) => this.receiveEvents(events));

    const initial = this.bridge.latestSnapshot();
    if (initial) this.receiveSnapshot(initial);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeSnapshot?.();
      this.unsubscribeEvents?.();
      this.unsubscribeSnapshot = null;
      this.unsubscribeEvents = null;
      this.labels.forEach((label) => label.destroy());
      this.labels.clear();
      this.landingLabel?.destroy();
      this.landingLabel = null;
      this.comparisonBadge?.destroy();
      this.comparisonBadge = null;
      this.mascot?.destroy();
      this.mascot = null;
      this.generatedBackground?.destroy();
      this.generatedBackground = null;
      this.bubbleImages.forEach((image) => image.destroy());
      this.bubbleImages.clear();
      this.visibleBubbleImages.clear();
    });
  }

  update(time: number): void {
    if (this.atmosphere && !this.reducedMotion) {
      this.atmosphere.alpha = 0.42 + Math.sin(time * 0.0011) * 0.08;
    }
    if (!this.view || this.compact) return;
    const aim = this.readAim();
    if (Math.abs(aim - this.lastGuideAim) > 0.05) {
      this.drawGuideAndLauncher(aim);
      this.syncMascot(aim);
    } else if (this.mascotOverrideFrame !== null && this.time.now >= this.mascotOverrideUntil) {
      this.mascotOverrideFrame = null;
      this.syncMascot(aim);
    }
  }

  refreshFocus(): void {
    const state = this.bridge.latestSnapshot();
    if (state) this.receiveSnapshot(state);
  }

  private drawLabChamber(): void {
    const background = this.add.graphics().setDepth(-20);
    background.fillGradientStyle(0x0b2030, 0x081824, 0x030a12, 0x07131d, 1);
    background.fillRect(0, 0, this.renderWidth, this.renderHeight);

    background.lineStyle(1, 0x70e8ef, 0.055);
    for (let x = 0; x <= this.renderWidth; x += 40) background.lineBetween(x, 0, x, this.renderHeight);
    for (let y = 0; y <= this.renderHeight; y += 40) background.lineBetween(0, y, this.renderWidth, y);

    if (this.portrait) {
      background.fillStyle(0x07131f, 0.88);
      background.fillRoundedRect(18, 18, this.renderWidth - 36, this.renderHeight - 36, 22);
      background.lineStyle(2, 0x67e6ec, 0.16);
      background.strokeRoundedRect(18, 18, this.renderWidth - 36, this.renderHeight - 36, 22);
      background.lineStyle(4, 0x0e3446, 1);
      background.lineBetween(38, 76, 38, this.renderHeight - 76);
      background.lineBetween(this.renderWidth - 38, 76, this.renderWidth - 38, this.renderHeight - 76);
      background.lineStyle(2, 0x36dbe5, 0.22);
      background.strokeCircle(92, 142, 42);
      background.lineStyle(2, 0xff806f, 0.22);
      background.strokeCircle(this.renderWidth - 92, 142, 42);
      background.lineStyle(1, 0xbafaff, 0.12);
      background.strokeCircle(this.renderWidth / 2, 150, 112);
      this.atmosphere = this.add.graphics().setDepth(-18);
      this.atmosphere.fillStyle(0xa5f8ff, 0.38);
      [
        [67, 266, 4], [92, 314, 6], [53, 381, 3],
        [this.renderWidth - 63, 286, 5], [this.renderWidth - 91, 356, 3],
        [118, 826, 4], [this.renderWidth - 124, 914, 5]
      ].forEach(([x, y, radius]) => this.atmosphere?.fillCircle(x ?? 0, y ?? 0, radius ?? 1));
      return;
    }

    background.fillStyle(0x07131f, 0.88);
    background.fillRoundedRect(22, 22, this.renderWidth - 44, this.renderHeight - 44, 22);
    background.lineStyle(2, 0x67e6ec, 0.16);
    background.strokeRoundedRect(22, 22, this.renderWidth - 44, this.renderHeight - 44, 22);
    background.lineStyle(5, 0x0e3446, 1);
    background.lineBetween(52, 91, 52, this.renderHeight - 91);
    background.lineBetween(this.renderWidth - 52, 91, this.renderWidth - 52, this.renderHeight - 91);

    // Original foam-lab hardware: pressure reservoirs, conduits, and scanner rings.
    for (const x of [66, 1214]) {
      background.fillStyle(0x0d2d3e, 1);
      background.fillRoundedRect(x - 18, 108, 36, 154, 17);
      background.lineStyle(2, x < 640 ? 0x36dbe5 : 0xff806f, 0.55);
      background.strokeRoundedRect(x - 18, 108, 36, 154, 17);
      background.fillStyle(x < 640 ? 0x36dbe5 : 0xff806f, 0.2);
      background.fillRoundedRect(x - 12, 176, 24, 78, 11);
      background.lineStyle(3, 0x1c5669, 0.65);
      background.lineBetween(x, 263, x, this.renderHeight - 108);
    }

    const scannerY = this.renderHeight - 128;
    background.lineStyle(1, 0xbafaff, 0.13);
    background.strokeCircle(168, scannerY, 62);
    background.strokeCircle(1112, scannerY, 62);
    background.strokeCircle(168, scannerY, 39);
    background.strokeCircle(1112, scannerY, 39);
    background.lineStyle(3, 0x36dbe5, 0.13);
    background.beginPath();
    background.arc(168, scannerY, 51, 3.65, 5.58);
    background.strokePath();
    background.lineStyle(3, 0xff806f, 0.13);
    background.beginPath();
    background.arc(1112, scannerY, 51, 0.55, 2.48);
    background.strokePath();

    this.atmosphere = this.add.graphics().setDepth(-18);
    this.atmosphere.fillStyle(0xa5f8ff, 0.38);
    [
      [93, 318, 3], [117, 344, 6], [82, 376, 4], [1190, 306, 5],
      [1168, 337, 3], [1196, 381, 7], [340, 92, 3], [938, 118, 4]
    ].forEach(([x, y, radius]) => this.atmosphere?.fillCircle(x ?? 0, y ?? 0, radius ?? 1));
  }

  private receiveSnapshot(state: PuzzleMatchState): void {
    this.view = toPuzzleViewModel(state, this.focus.playerId);
    this.layouts = this.calculateLayouts(this.view);
    this.visibleBubbleImages.clear();
    this.drawBoards(this.view);
    this.syncLabels(this.view);
    const aim = this.compact ? this.primaryBoard()?.aim ?? 0 : this.readAim();
    this.drawGuideAndLauncher(aim);
    this.syncMascot(aim);
    this.syncBubbleImageVisibility();
  }

  private calculateLayouts(view: PuzzleViewModel): Map<string, BoardLayout> {
    const layouts = new Map<string, BoardLayout>();
    const primary = view.boards.find((board) => board.playerId === view.primaryPlayerId) ?? view.boards[0];
    if (!primary) return layouts;
    const others = view.boards.filter((board) => board.playerId !== primary.playerId);

    if (this.compact) {
      const count = view.boards.length;
      const scale = count <= 2 ? 0.92 : count === 3 ? 0.66 : 0.62;
      const boardWidth = view.logicalBoard.width * scale;
      const gap = count <= 2 ? 92 : 24;
      const totalWidth = boardWidth * count + gap * Math.max(0, count - 1);
      const startX = (this.renderWidth - totalWidth) / 2;
      const y = Math.max(46, (this.renderHeight - view.logicalBoard.height * scale) / 2 + 12);
      view.boards.forEach((board, index) => layouts.set(board.playerId, {
        board,
        x: startX + index * (boardWidth + gap),
        y,
        scale,
        primary: board.playerId === primary.playerId
      }));
      return layouts;
    }

    if (this.portrait) {
      const primaryScale = PORTRAIT_PRIMARY_SCALE;
      layouts.set(primary.playerId, {
        board: primary,
        x: PORTRAIT_PRIMARY_X,
        y: PORTRAIT_PRIMARY_Y,
        scale: primaryScale,
        primary: true
      });
      const comparisons = [...others].sort((a, b) => Number(a.team === primary.team) - Number(b.team === primary.team));
      comparisons.forEach((opponent, index) => {
        const scale = comparisons.length === 1 ? PORTRAIT_OPPONENT_SCALE : 0.3;
        layouts.set(opponent.playerId, {
          board: opponent,
          x: comparisons.length === 1 ? PORTRAIT_OPPONENT_X : this.renderWidth - view.logicalBoard.width * scale - 24,
          y: PORTRAIT_OPPONENT_Y + index * 250,
          scale,
          primary: false
        });
      });
      return layouts;
    }

    if (this.duelComparison && view.mode === "1v1" && others[0]) {
      const boardWidth = view.logicalBoard.width * DUEL_COMPARISON_SCALE;
      const startX = (this.renderWidth - boardWidth * 2 - DUEL_COMPARISON_GAP) / 2;
      layouts.set(primary.playerId, {
        board: primary,
        x: startX,
        y: DUEL_COMPARISON_Y,
        scale: DUEL_COMPARISON_SCALE,
        primary: true
      });
      layouts.set(others[0].playerId, {
        board: others[0],
        x: startX + boardWidth + DUEL_COMPARISON_GAP,
        y: DUEL_COMPARISON_Y,
        scale: DUEL_COMPARISON_SCALE,
        primary: false
      });
      return layouts;
    }

    const primaryScale = LANDSCAPE_PRIMARY_SCALE;
    layouts.set(primary.playerId, {
      board: primary,
      x: (this.renderWidth - view.logicalBoard.width * primaryScale) / 2,
      y: LANDSCAPE_PRIMARY_Y,
      scale: primaryScale,
      primary: true
    });

    if (others.length === 1) {
      layouts.set(others[0]!.playerId, { board: others[0]!, x: 976, y: 184, scale: 0.62, primary: false });
    } else if (others.length > 1) {
      const teammate = others.find((board) => board.team === primary.team);
      const opponents = others.filter((board) => board !== teammate);
      if (teammate) layouts.set(teammate.playerId, { board: teammate, x: 84, y: 184, scale: 0.62, primary: false });
      opponents.slice(0, 2).forEach((board, index) => layouts.set(board.playerId, {
        board,
        x: 1012,
        y: 54 + index * 398,
        scale: 0.46,
        primary: false
      }));
    }
    return layouts;
  }

  private drawBoards(view: PuzzleViewModel): void {
    const graphics = this.boardLayer;
    if (!graphics) return;
    graphics.clear();
    this.layouts.forEach((layout) => this.drawBoard(graphics, view, layout));
    this.drawDuelComparisonDivider(graphics, view);
  }

  private drawDuelComparisonDivider(graphics: Phaser.GameObjects.Graphics, view: PuzzleViewModel): void {
    if (!this.duelComparison || view.mode !== "1v1" || this.layouts.size !== 2) {
      this.comparisonBadge?.setVisible(false);
      return;
    }
    const centerX = this.renderWidth / 2;
    graphics.lineStyle(1, 0xffd166, 0.28);
    graphics.lineBetween(centerX, 66, centerX, this.renderHeight - 54);
    graphics.lineStyle(3, 0xffd166, 0.66);
    graphics.strokeCircle(centerX, this.renderHeight / 2, 27);
    graphics.lineStyle(1, 0xc6fcff, 0.2);
    graphics.strokeCircle(centerX, this.renderHeight / 2, 34);
    if (!this.comparisonBadge) {
      this.comparisonBadge = this.add.text(centerX, this.renderHeight / 2, "VS", {
        fontFamily: "Arial Narrow, Avenir Next Condensed, sans-serif",
        fontSize: "18px",
        color: "#ffe09a",
        fontStyle: "bold",
        stroke: "#02070c",
        strokeThickness: 4
      }).setOrigin(0.5).setDepth(8);
    }
    this.comparisonBadge.setPosition(centerX, this.renderHeight / 2).setVisible(true);
  }

  private drawBoard(graphics: Phaser.GameObjects.Graphics, view: PuzzleViewModel, layout: BoardLayout): void {
    const { board, x, y, scale, primary } = layout;
    const width = view.logicalBoard.width * scale;
    const height = view.logicalBoard.height * scale;
    const frame = TEAM_STYLE[board.accent];
    const identity = TEAM_STYLE[board.team];
    const featured = primary || (this.duelComparison && view.mode === "1v1");
    const framePad = featured ? 13 : 8;

    graphics.fillStyle(0x02070c, 0.72);
    graphics.fillRoundedRect(x - framePad, y - framePad, width + framePad * 2, height + framePad * 2, featured ? 12 : 7);
    graphics.lineStyle(featured ? 3 : 2, frame.accent, board.connected ? 0.72 : 0.25);
    graphics.strokeRoundedRect(x - framePad, y - framePad, width + framePad * 2, height + framePad * 2, featured ? 12 : 7);
    graphics.lineStyle(1, frame.pale, 0.18);
    graphics.strokeRoundedRect(x - framePad + 5, y - framePad + 5, width + framePad * 2 - 10, height + framePad * 2 - 10, 7);

    graphics.fillStyle(0x071521, 1);
    graphics.fillRect(x, y, width, height);
    const tileWidth = width / view.logicalBoard.columns;
    const tileHeight = view.logicalBoard.hexRowHeight * scale;
    for (let row = 0; row < view.logicalBoard.rows; row += 1) {
      for (let col = 0; col < view.logicalBoard.columns; col += 1) {
        graphics.fillStyle((row + col) % 2 === 0 ? 0x102837 : 0x0b1f2c, featured ? 0.66 : 0.5);
        graphics.fillRect(x + col * tileWidth, y + row * tileHeight, tileWidth, tileHeight);
      }
    }
    graphics.lineStyle(1, 0x87edf1, featured ? 0.07 : 0.04);
    for (let col = 1; col < view.logicalBoard.columns; col += 1) {
      graphics.lineBetween(x + col * tileWidth, y, x + col * tileWidth, y + height);
    }

    const dangerY = y + board.dangerLineY * scale;
    graphics.lineStyle(featured ? 2 : 1, 0xff637d, board.status === "active" ? 0.6 : 0.92);
    graphics.lineBetween(x + 3, dangerY, x + width - 3, dangerY);
    for (let notch = x + 7; notch < x + width - 8; notch += 15 * scale) {
      graphics.fillStyle(0xff637d, featured ? 0.36 : 0.2);
      graphics.fillTriangle(notch, dangerY - 4 * scale, notch + 5 * scale, dangerY, notch, dangerY + 4 * scale);
    }

    board.cells.forEach((cell) => {
      const pointX = view.logicalBoard.bubbleRadius + cell.col * view.logicalBoard.bubbleRadius * 2
        + (cell.row % 2 === 1 ? view.logicalBoard.bubbleRadius : 0);
      const pointY = view.logicalBoard.bubbleRadius + cell.row * view.logicalBoard.hexRowHeight;
      const orbX = x + pointX * scale;
      const orbY = y + pointY * scale;
      const orbRadius = view.logicalBoard.bubbleRadius * scale * 0.93;
      if (!this.drawAtlasOrb(
        `board:${board.playerId}:cell:${cell.row}:${cell.col}`,
        orbX,
        orbY,
        orbRadius,
        cell.color,
        "normal",
        featured ? 2 : 1
      )) {
        this.drawOrb(graphics, orbX, orbY, orbRadius, cell.color);
      }
    });

    if (board.activeShot) {
      const shotX = x + board.activeShot.x * scale;
      const shotY = y + board.activeShot.y * scale;
      graphics.fillStyle(puzzleColorStyle(board.activeShot.color).light, 0.12);
      graphics.fillCircle(shotX, shotY + 10 * scale, view.logicalBoard.bubbleRadius * scale * 1.25);
      const shotRadius = view.logicalBoard.bubbleRadius * scale * 0.94;
      if (!this.drawAtlasOrb(
        `board:${board.playerId}:active-shot`,
        shotX,
        shotY,
        shotRadius,
        board.activeShot.color,
        board.activeShot.kind,
        featured ? 4 : 2
      )) {
        this.drawOrb(graphics, shotX, shotY, shotRadius, board.activeShot.color, board.activeShot.kind);
      }
    }

    if (board.pendingPressureRows > 0 || board.pendingAttackBubbles > 0) {
      const segments = Math.min(3, Math.max(board.pendingPressureRows, Math.ceil(board.pendingAttackBubbles / 8)));
      for (let index = 0; index < segments; index += 1) {
        graphics.fillStyle(0xff637d, 0.88 - index * 0.16);
        graphics.fillRoundedRect(x + width + 5, y + height - (index + 1) * 17 * scale, Math.max(3, 5 * scale), 12 * scale, 2);
      }
    }

    this.drawTeamMarker(graphics, x + 12 * scale, y + height - 15 * scale, Math.max(4, 6 * scale), board.team, identity.accent);
    if (!board.connected) {
      graphics.lineStyle(2, 0xffcb54, 0.62);
      graphics.lineBetween(x + 12, y + height - 12, x + width - 12, y + 12);
    }
    if (board.status !== "active") {
      graphics.fillStyle(board.status === "cleared" ? 0x65e6b2 : 0xff637d, 0.15);
      graphics.fillRect(x, y, width, height);
      graphics.lineStyle(featured ? 7 : 4, board.status === "cleared" ? 0x65e6b2 : 0xff637d, 0.8);
      graphics.lineBetween(x + width * 0.27, y + height * 0.5, x + width * 0.44, y + height * 0.66);
      graphics.lineBetween(x + width * 0.44, y + height * 0.66, x + width * 0.75, y + height * 0.31);
    }

    // Machined corner brackets keep the board frame lab-like without tracing a cabinet.
    graphics.lineStyle(featured ? 4 : 2, frame.pale, 0.68);
    const corner = (featured ? 18 : 10) * scale;
    graphics.lineBetween(x - framePad, y + corner, x - framePad, y - framePad);
    graphics.lineBetween(x - framePad, y - framePad, x + corner, y - framePad);
    graphics.lineBetween(x + width - corner, y + height + framePad, x + width + framePad, y + height + framePad);
    graphics.lineBetween(x + width + framePad, y + height + framePad, x + width + framePad, y + height - corner);
  }

  private drawOrb(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    color: string,
    kind: PuzzleBubbleKindView = "normal"
  ): void {
    const style = puzzleColorStyle(color);
    graphics.fillStyle(0x000000, 0.32);
    graphics.fillCircle(x + radius * 0.12, y + radius * 0.18, radius * 1.04);
    graphics.fillStyle(style.dark, 1);
    graphics.fillCircle(x, y, radius);
    graphics.fillStyle(style.fill, 1);
    graphics.fillCircle(x - radius * 0.04, y - radius * 0.07, radius * 0.88);
    graphics.fillStyle(style.light, 0.72);
    graphics.fillCircle(x - radius * 0.34, y - radius * 0.36, Math.max(1.2, radius * 0.19));
    graphics.lineStyle(Math.max(1, radius * 0.075), style.light, 0.5);
    graphics.beginPath();
    graphics.arc(x, y, radius * 0.76, 3.52, 5.44);
    graphics.strokePath();

    graphics.lineStyle(Math.max(1, radius * 0.1), style.light, kind === "normal" ? 0.72 : 0.94);
    const symbol = radius * 0.28;
    if (kind === "color_burst") {
      graphics.strokeCircle(x, y, radius * 0.55);
      graphics.lineStyle(Math.max(1, radius * 0.075), 0xffffff, 0.95);
      this.drawStar(graphics, x, y, symbol * 1.06, symbol * 0.45);
      graphics.lineStyle(Math.max(1, radius * 0.08), style.light, 0.78);
      graphics.strokeCircle(x, y, radius * 1.08);
      return;
    }
    if (kind === "row_burst") {
      graphics.lineBetween(x - radius * 0.58, y - symbol * 0.36, x + radius * 0.58, y - symbol * 0.36);
      graphics.lineBetween(x - radius * 0.58, y + symbol * 0.36, x + radius * 0.58, y + symbol * 0.36);
      graphics.fillStyle(style.light, 0.94);
      graphics.fillTriangle(x - radius * 0.74, y, x - radius * 0.52, y - symbol * 0.62, x - radius * 0.52, y + symbol * 0.62);
      graphics.fillTriangle(x + radius * 0.74, y, x + radius * 0.52, y - symbol * 0.62, x + radius * 0.52, y + symbol * 0.62);
      return;
    }
    if (kind === "column_burst") {
      graphics.lineBetween(x - symbol * 0.36, y - radius * 0.58, x - symbol * 0.36, y + radius * 0.58);
      graphics.lineBetween(x + symbol * 0.36, y - radius * 0.58, x + symbol * 0.36, y + radius * 0.58);
      graphics.fillStyle(style.light, 0.94);
      graphics.fillTriangle(x, y - radius * 0.74, x - symbol * 0.62, y - radius * 0.52, x + symbol * 0.62, y - radius * 0.52);
      graphics.fillTriangle(x, y + radius * 0.74, x - symbol * 0.62, y + radius * 0.52, x + symbol * 0.62, y + radius * 0.52);
      return;
    }
    switch (style.shape) {
      case "triangle":
        graphics.strokeTriangle(x, y - symbol, x - symbol, y + symbol * 0.75, x + symbol, y + symbol * 0.75);
        break;
      case "diamond":
        graphics.beginPath();
        graphics.moveTo(x, y - symbol);
        graphics.lineTo(x + symbol, y);
        graphics.lineTo(x, y + symbol);
        graphics.lineTo(x - symbol, y);
        graphics.closePath();
        graphics.strokePath();
        break;
      case "ring":
        graphics.strokeCircle(x, y, symbol * 0.75);
        break;
      case "hex":
        graphics.beginPath();
        for (let index = 0; index < 6; index += 1) {
          const angle = Math.PI / 3 * index - Math.PI / 2;
          const pointX = x + Math.cos(angle) * symbol;
          const pointY = y + Math.sin(angle) * symbol;
          if (index === 0) graphics.moveTo(pointX, pointY);
          else graphics.lineTo(pointX, pointY);
        }
        graphics.closePath();
        graphics.strokePath();
        break;
      default:
        graphics.lineBetween(x - symbol, y, x + symbol, y);
        graphics.lineBetween(x, y - symbol, x, y + symbol);
        break;
    }
  }

  private drawAtlasOrb(
    poolKey: string,
    x: number,
    y: number,
    radius: number,
    color: string,
    kind: PuzzleBubbleKindView,
    depth: number
  ): boolean {
    if (!this.textures.exists(FOAM_POP_TEXTURE_KEYS.bubbles)) return false;
    let image = this.bubbleImages.get(poolKey);
    const frame = foamPopBubbleFrame(color, kind);
    if (!image) {
      image = this.add.image(x, y, FOAM_POP_TEXTURE_KEYS.bubbles, frame).setOrigin(0.5);
      this.bubbleImages.set(poolKey, image);
    } else {
      image.setTexture(FOAM_POP_TEXTURE_KEYS.bubbles, frame);
    }
    // The 64 px cells include a small transparent safety gutter around each orb.
    image.setPosition(x, y).setDisplaySize(radius * 2.18, radius * 2.18).setDepth(depth).setVisible(true);
    this.visibleBubbleImages.add(poolKey);
    return true;
  }

  private syncBubbleImageVisibility(): void {
    this.bubbleImages.forEach((image, key) => image.setVisible(this.visibleBubbleImages.has(key)));
  }

  private drawStar(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    outerRadius: number,
    innerRadius: number
  ): void {
    graphics.beginPath();
    for (let index = 0; index < 10; index += 1) {
      const angle = -Math.PI / 2 + index * Math.PI / 5;
      const radius = index % 2 === 0 ? outerRadius : innerRadius;
      const pointX = x + Math.cos(angle) * radius;
      const pointY = y + Math.sin(angle) * radius;
      if (index === 0) graphics.moveTo(pointX, pointY);
      else graphics.lineTo(pointX, pointY);
    }
    graphics.closePath();
    graphics.strokePath();
  }

  private drawTeamMarker(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    team: PuzzleTeamView,
    color: number
  ): void {
    graphics.lineStyle(Math.max(1.5, radius * 0.28), color, 0.9);
    if (team === "blue") {
      graphics.strokeTriangle(x, y - radius, x - radius, y + radius, x + radius, y + radius);
    } else {
      graphics.beginPath();
      graphics.moveTo(x, y - radius);
      graphics.lineTo(x + radius, y);
      graphics.lineTo(x, y + radius);
      graphics.lineTo(x - radius, y);
      graphics.closePath();
      graphics.strokePath();
    }
  }

  private drawGuideAndLauncher(aim: number): void {
    const view = this.view;
    const layout = this.primaryLayout();
    const guide = this.guideLayer;
    const launcher = this.launcherLayer;
    if (!view || !layout || !guide || !launcher) return;
    this.lastGuideAim = aim;
    guide.clear();
    launcher.clear();

    const board = layout.board;
    const scale = layout.scale;
    const radius = view.logicalBoard.bubbleRadius;
    let pointX = view.logicalBoard.shooterX;
    let pointY = view.logicalBoard.shooterY - radius * 1.15;
    const radians = Phaser.Math.DegToRad(aim);
    let directionX = Math.sin(radians);
    const directionY = -Math.cos(radians);
    const step = 17;
    let landingX = pointX;
    let landingY = pointY;
    let foundLanding = false;

    const verticalStep = Math.max(0.1, Math.cos(radians)) * step;
    const fullGuideSteps = Math.min(180, Math.ceil((view.logicalBoard.shooterY + radius) / verticalStep) + 4);
    const guidePolicy = puzzleGuidePolicy(board.shotsResolved, view.logicalBoard.hexRowHeight, step);
    const maxGuideSteps = guidePolicy.maxSteps ?? fullGuideSteps;
    for (let index = 0; index < maxGuideSteps; index += 1) {
      pointX += directionX * step;
      pointY += directionY * step;
      if (pointX < radius) {
        pointX = radius + (radius - pointX);
        directionX *= -1;
      } else if (pointX > view.logicalBoard.width - radius) {
        pointX = view.logicalBoard.width - radius - (pointX - (view.logicalBoard.width - radius));
        directionX *= -1;
      }
      if (pointY <= radius) {
        landingX = pointX;
        landingY = radius;
        foundLanding = true;
        break;
      }
      const collides = board.cells.some((cell) => {
        const cellX = radius + cell.col * radius * 2 + (cell.row % 2 === 1 ? radius : 0);
        const cellY = radius + cell.row * view.logicalBoard.hexRowHeight;
        return Math.hypot(pointX - cellX, pointY - cellY) <= radius * 2.05;
      });
      if (guidePolicy.limited || index % 2 === 0) {
        const alpha = guidePolicy.limited
          ? Math.max(0.56, 0.9 - index / 18)
          : Math.max(0.16, 0.72 - index / 110);
        guide.fillStyle(collides ? 0xffcb54 : 0xd9ffff, alpha);
        guide.fillCircle(
          layout.x + pointX * scale,
          layout.y + pointY * scale,
          Math.max(guidePolicy.limited ? 2 : 1.4, (guidePolicy.limited ? 2.8 : 2.25) * scale)
        );
      }
      if (collides) {
        landingX = pointX;
        landingY = pointY;
        foundLanding = true;
        break;
      }
      landingX = pointX;
      landingY = pointY;
    }

    if (foundLanding || (!guidePolicy.limited && landingY < view.logicalBoard.dangerLineY)) {
      const markerX = layout.x + landingX * scale;
      const markerY = layout.y + landingY * scale;
      const markerRadius = radius * scale * 0.68;
      guide.lineStyle(Math.max(1.25, 1.7 * scale), 0xffd166, this.compact ? 0.42 : 0.8);
      guide.strokeCircle(markerX, markerY, markerRadius);
      guide.lineBetween(markerX - markerRadius * 0.52, markerY, markerX + markerRadius * 0.52, markerY);
      guide.lineBetween(markerX, markerY - markerRadius * 0.52, markerX, markerY + markerRadius * 0.52);
      if (this.landingLabel) {
        this.landingLabel.setText(board.currentKind === "normal" ? "LAND" : `LAND · ${puzzleBubbleKindLabel(board.currentKind)}`);
        this.landingLabel.setPosition(markerX, markerY - markerRadius - 3);
        this.landingLabel.setVisible(!this.compact || layout.primary);
      }
    } else {
      this.landingLabel?.setVisible(false);
    }

    const launcherX = layout.x + view.logicalBoard.shooterX * scale;
    const launcherY = layout.y + view.logicalBoard.shooterY * scale;
    const vectorX = Math.sin(radians);
    const vectorY = -Math.cos(radians);
    const sideX = Math.cos(radians);
    const sideY = Math.sin(radians);
    const barrelLength = 42 * scale;
    const barrelHalf = 7 * scale;
    launcher.fillStyle(0x0c3446, 1);
    launcher.beginPath();
    launcher.moveTo(launcherX + sideX * barrelHalf, launcherY + sideY * barrelHalf);
    launcher.lineTo(launcherX + vectorX * barrelLength + sideX * barrelHalf, launcherY + vectorY * barrelLength + sideY * barrelHalf);
    launcher.lineTo(launcherX + vectorX * barrelLength - sideX * barrelHalf, launcherY + vectorY * barrelLength - sideY * barrelHalf);
    launcher.lineTo(launcherX - sideX * barrelHalf, launcherY - sideY * barrelHalf);
    launcher.closePath();
    launcher.fillPath();
    launcher.lineStyle(2.2 * scale, TEAM_STYLE[board.accent].pale, 0.72);
    launcher.strokePath();
    launcher.fillStyle(0x102b3b, 1);
    launcher.fillCircle(launcherX, launcherY, 28 * scale);
    launcher.lineStyle(3 * scale, TEAM_STYLE[board.accent].accent, 0.82);
    launcher.strokeCircle(launcherX, launcherY, 28 * scale);
    const currentY = launcherY - 2 * scale;
    const currentRadius = radius * scale * 0.92;
    if (!this.drawAtlasOrb(
      `board:${board.playerId}:launcher-current`,
      launcherX,
      currentY,
      currentRadius,
      board.currentColor,
      board.currentKind,
      6
    )) {
      this.drawOrb(launcher, launcherX, currentY, currentRadius, board.currentColor, board.currentKind);
    }

    // The queued orb sits in an independent side cradle, visibly distinct from current.
    const nextX = launcherX + 55 * scale;
    launcher.lineStyle(2 * scale, 0x84e9ee, 0.24);
    launcher.strokeCircle(nextX, launcherY + 7 * scale, radius * scale * 0.78);
    const nextY = launcherY + 7 * scale;
    const nextRadius = radius * scale * 0.62;
    if (!this.drawAtlasOrb(
      `board:${board.playerId}:launcher-next`,
      nextX,
      nextY,
      nextRadius,
      board.nextColor,
      board.nextKind,
      6
    )) {
      this.drawOrb(launcher, nextX, nextY, nextRadius, board.nextColor, board.nextKind);
    }

    if (this.duelComparison) {
      const opponent = [...this.layouts.values()].find((candidate) => !candidate.primary);
      if (opponent) this.drawPassiveLauncher(view, opponent, opponent.board.aim, launcher);
    }
  }

  private drawPassiveLauncher(
    view: PuzzleViewModel,
    layout: BoardLayout,
    aim: number,
    launcher: Phaser.GameObjects.Graphics
  ): void {
    const board = layout.board;
    const scale = layout.scale;
    const radius = view.logicalBoard.bubbleRadius;
    const launcherX = layout.x + view.logicalBoard.shooterX * scale;
    const launcherY = layout.y + view.logicalBoard.shooterY * scale;
    const radians = Phaser.Math.DegToRad(aim);
    const vectorX = Math.sin(radians);
    const vectorY = -Math.cos(radians);
    const sideX = Math.cos(radians);
    const sideY = Math.sin(radians);
    const barrelLength = 42 * scale;
    const barrelHalf = 7 * scale;

    launcher.fillStyle(0x0c3446, 1);
    launcher.beginPath();
    launcher.moveTo(launcherX + sideX * barrelHalf, launcherY + sideY * barrelHalf);
    launcher.lineTo(launcherX + vectorX * barrelLength + sideX * barrelHalf, launcherY + vectorY * barrelLength + sideY * barrelHalf);
    launcher.lineTo(launcherX + vectorX * barrelLength - sideX * barrelHalf, launcherY + vectorY * barrelLength - sideY * barrelHalf);
    launcher.lineTo(launcherX - sideX * barrelHalf, launcherY - sideY * barrelHalf);
    launcher.closePath();
    launcher.fillPath();
    launcher.lineStyle(2.2 * scale, TEAM_STYLE[board.accent].pale, 0.72);
    launcher.strokePath();
    launcher.fillStyle(0x102b3b, 1);
    launcher.fillCircle(launcherX, launcherY, 28 * scale);
    launcher.lineStyle(3 * scale, TEAM_STYLE[board.accent].accent, 0.82);
    launcher.strokeCircle(launcherX, launcherY, 28 * scale);

    const currentY = launcherY - 2 * scale;
    const currentRadius = radius * scale * 0.92;
    if (!this.drawAtlasOrb(
      `board:${board.playerId}:launcher-current`,
      launcherX,
      currentY,
      currentRadius,
      board.currentColor,
      board.currentKind,
      6
    )) {
      this.drawOrb(launcher, launcherX, currentY, currentRadius, board.currentColor, board.currentKind);
    }

    const nextX = launcherX + 55 * scale;
    const nextY = launcherY + 7 * scale;
    const nextRadius = radius * scale * 0.62;
    launcher.lineStyle(2 * scale, TEAM_STYLE[board.accent].pale, 0.24);
    launcher.strokeCircle(nextX, nextY, radius * scale * 0.78);
    if (!this.drawAtlasOrb(
      `board:${board.playerId}:launcher-next`,
      nextX,
      nextY,
      nextRadius,
      board.nextColor,
      board.nextKind,
      6
    )) {
      this.drawOrb(launcher, nextX, nextY, nextRadius, board.nextColor, board.nextKind);
    }
  }

  private syncLabels(view: PuzzleViewModel): void {
    const activeIds = new Set(this.layouts.keys());
    this.labels.forEach((label, playerId) => {
      if (!activeIds.has(playerId)) {
        label.destroy();
        this.labels.delete(playerId);
      }
    });
    this.layouts.forEach((layout) => {
      const detailed = layout.primary || this.duelComparison;
      let label = this.labels.get(layout.board.playerId);
      if (!label) {
        label = this.add.text(0, 0, "", {
          fontFamily: "Arial Narrow, Avenir Next Condensed, sans-serif",
          fontSize: detailed ? "15px" : "12px",
          color: "#dffcff",
          fontStyle: "bold",
          backgroundColor: "rgba(2, 12, 20, 0.86)",
          padding: detailed
            ? { left: 6, right: 6, top: 3, bottom: 3 }
            : { left: 4, right: 4, top: 2, bottom: 2 }
        });
        label.setOrigin(0, 1);
        label.setDepth(7);
        this.labels.set(layout.board.playerId, label);
      }
      const marker = layout.board.team === "blue" ? "△" : "◇";
      const suffix = layout.board.kind === "bot" && !this.duelComparison ? " · AI" : "";
      const combo = layout.board.combo > 0 ? ` · ×${layout.board.combo}` : "";
      const pressureRows = layout.board.pendingPressureRows > 0 ? ` +${layout.board.pendingPressureRows}R` : "";
      const pressureBubbles = layout.board.pendingAttackBubbles > 0 ? ` +${layout.board.pendingAttackBubbles}B` : "";
      const pressure = pressureRows || pressureBubbles ? ` · P${pressureRows}${pressureBubbles}` : "";
      const name = compactPlayerName(layout.board.name, detailed ? 14 : layout.scale >= 0.55 ? 10 : 8);
      label.setText(this.duelComparison
        ? `${layout.primary ? "MY BOARD" : "AI BOARD"} · ${marker} ${name} · ${layout.board.score}${combo}${pressure}`
        : layout.primary
          ? `${marker} ${name}${suffix} · ${layout.board.score}${combo}${pressure}`
          : `${marker} ${name}${suffix} · ${layout.board.score}`);
      label.setColor(layout.board.team === "blue" ? "#c6fcff" : "#ffd9cb");
      label.setPosition(layout.x - (detailed ? 4 : 2), layout.y - (detailed ? 10 : 7));
      label.setFontSize(detailed ? 14 : layout.scale >= 0.55 ? 10 : 9);
      label.setPadding(
        detailed ? 6 : 4,
        detailed ? 3 : 2,
        detailed ? 6 : 4,
        detailed ? 3 : 2
      );
    });
  }

  private receiveEvents(events: PuzzleEvent[]): void {
    for (const event of events) {
      const raw = rawRecord(event);
      const type = typeof raw.type === "string" ? raw.type : "";
      this.receiveMascotEvent(type, raw);
      const summary = summarizePuzzleEvent(event);
      if (!summary) continue;
      if (type === "pressure_queued") {
        const fromId = typeof raw.fromPlayerId === "string" ? raw.fromPlayerId : undefined;
        const targetId = typeof raw.targetPlayerId === "string" ? raw.targetPlayerId : undefined;
        const fromLayout = fromId ? this.layouts.get(fromId) : undefined;
        const targetLayout = targetId ? this.layouts.get(targetId) : undefined;
        if (fromLayout && targetLayout) {
          this.spawnPressureTransfer(fromLayout, targetLayout, raw);
          continue;
        }
      }
      const playerId = summary.playerId
        ?? (typeof raw.fromPlayerId === "string" ? raw.fromPlayerId : undefined)
        ?? this.view?.primaryPlayerId;
      const layout = playerId ? this.layouts.get(playerId) : this.primaryLayout();
      if (!layout) continue;
      const toneColor = summary.tone === "danger" ? 0xff637d
        : summary.tone === "orange" ? TEAM_STYLE.orange.accent
        : summary.tone === "blue" ? TEAM_STYLE.blue.accent
        : 0xffd166;
      if (type === "special_triggered") this.spawnSpecialImpact(layout, toneColor, raw);
      else if (type === "combo_changed" || type === "combo_updated") this.spawnComboCallout(layout, raw, false);
      else if (type === "combo_broken") this.spawnComboCallout(layout, raw, true);
      else this.spawnImpact(layout, toneColor, raw);
    }
  }

  private spawnPressureTransfer(
    from: BoardLayout,
    target: BoardLayout,
    event: Record<string, unknown>
  ): void {
    const view = this.view;
    if (!view) return;
    const fromX = from.x + view.logicalBoard.width * from.scale * 0.5;
    const fromY = from.y + view.logicalBoard.height * from.scale * 0.28;
    const targetX = target.x + view.logicalBoard.width * target.scale * 0.5;
    const targetY = target.y + view.logicalBoard.height * target.scale * 0.28;
    const bubbles = typeof event.bubbles === "number" ? Math.max(0, event.bubbles) : 0;
    const rows = typeof event.rows === "number" ? Math.max(0, event.rows) : 0;
    const combo = typeof event.combo === "number" ? Math.max(0, event.combo) : 0;
    const color = TEAM_STYLE[from.board.team].accent;
    const trail = this.add.graphics().setDepth(12);
    trail.lineStyle(2, color, this.reducedMotion ? 0.45 : 0.22);
    trail.lineBetween(fromX, fromY, targetX, targetY);
    const packet = this.add.graphics({ x: fromX, y: fromY }).setDepth(13);
    const packetScale = Math.max(0.7, Math.min(1.25, from.scale));
    const satelliteCount = Math.min(4, Math.max(1, Math.ceil((bubbles || rows * 8) / 6)));
    packet.fillStyle(0x02070c, 0.9);
    packet.fillCircle(0, 0, 14 * packetScale);
    packet.lineStyle(2.4 * packetScale, color, 0.95);
    packet.strokeCircle(0, 0, 12 * packetScale);
    packet.fillStyle(color, 0.9);
    for (let index = 0; index < satelliteCount; index += 1) {
      const angle = index / satelliteCount * Math.PI * 2 - Math.PI / 2;
      packet.fillCircle(
        Math.cos(angle) * 6.5 * packetScale,
        Math.sin(angle) * 6.5 * packetScale,
        2.8 * packetScale
      );
    }
    if (combo > 1) {
      packet.lineStyle(1.4 * packetScale, 0xffd166, 0.9);
      packet.strokeCircle(0, 0, 16 * packetScale);
    }

    const finish = (): void => {
      trail.destroy();
      packet.destroy();
      this.spawnImpact(target, 0xff637d, event);
    };
    if (this.reducedMotion) {
      packet.setPosition(targetX, targetY);
      this.time.delayedCall(150, finish);
      return;
    }
    this.tweens.add({
      targets: packet,
      x: targetX,
      y: targetY,
      scaleX: 1.16,
      scaleY: 1.16,
      duration: 460,
      ease: "Cubic.easeInOut",
      onComplete: finish
    });
    this.tweens.add({
      targets: trail,
      alpha: 0,
      duration: 520,
      ease: "Quad.easeIn"
    });
  }

  private spawnSpecialImpact(
    layout: BoardLayout,
    color: number,
    event: Record<string, unknown>
  ): void {
    const view = this.view;
    if (!view) return;
    this.spawnImpact(layout, color, event);
    const kind = normalizePuzzleBubbleKind(event.kind ?? event.bubbleKind);
    const eventCells = Array.isArray(event.cells) ? event.cells : [];
    const firstCell = rawRecord(event.origin ?? eventCells[0] ?? rawRecord(event.cell));
    const row = typeof firstCell.row === "number" ? firstCell.row : Math.floor(view.logicalBoard.rows * 0.35);
    const col = typeof firstCell.col === "number" ? firstCell.col : Math.floor(view.logicalBoard.columns * 0.5);
    const cellX = layout.x + (
      view.logicalBoard.bubbleRadius + col * view.logicalBoard.bubbleRadius * 2
      + (row % 2 === 1 ? view.logicalBoard.bubbleRadius : 0)
    ) * layout.scale;
    const cellY = layout.y + (
      view.logicalBoard.bubbleRadius + row * view.logicalBoard.hexRowHeight
    ) * layout.scale;
    const width = view.logicalBoard.width * layout.scale;
    const height = view.logicalBoard.height * layout.scale;
    const effect = this.add.graphics().setDepth(11);
    effect.lineStyle(Math.max(2, 6 * layout.scale), color, 0.86);
    effect.fillStyle(color, 0.14);
    if (kind === "row_burst") {
      effect.fillRect(layout.x, cellY - 8 * layout.scale, width, 16 * layout.scale);
      effect.lineBetween(layout.x, cellY, layout.x + width, cellY);
    } else if (kind === "column_burst") {
      effect.fillRect(cellX - 8 * layout.scale, layout.y, 16 * layout.scale, height);
      effect.lineBetween(cellX, layout.y, cellX, layout.y + height);
    } else {
      effect.strokeCircle(cellX, cellY, Math.max(28, 68 * layout.scale));
      effect.lineStyle(Math.max(1.5, 3 * layout.scale), 0xffffff, 0.86);
      this.drawStar(effect, cellX, cellY, Math.max(18, 34 * layout.scale), Math.max(8, 15 * layout.scale));
    }
    if (this.reducedMotion) {
      this.time.delayedCall(140, () => effect.destroy());
      return;
    }
    this.tweens.add({
      targets: effect,
      alpha: 0,
      duration: 380,
      ease: "Cubic.easeOut",
      onComplete: () => effect.destroy()
    });
  }

  private spawnComboCallout(
    layout: BoardLayout,
    event: Record<string, unknown>,
    broken: boolean
  ): void {
    const view = this.view;
    if (!view) return;
    const combo = broken
      ? typeof event.previousCombo === "number"
        ? Math.max(0, event.previousCombo)
        : 0
      : typeof event.combo === "number"
        ? Math.max(0, event.combo)
        : 0;
    const bonusRows = typeof event.bonusRows === "number" ? Math.max(0, event.bonusRows) : 0;
    const label = broken
      ? `CHAIN RESET${combo > 1 ? ` · ×${combo}` : ""}`
      : `${combo > 0 ? `×${combo} COMBO` : "COMBO"}${bonusRows > 0 ? ` · +${bonusRows} ROW` : ""}`;
    const text = this.add.text(
      layout.x + view.logicalBoard.width * layout.scale * 0.5,
      layout.y + view.logicalBoard.height * layout.scale * 0.2,
      label,
      {
        fontFamily: "Arial Narrow, Avenir Next Condensed, sans-serif",
        fontSize: layout.primary ? "24px" : "13px",
        color: broken ? "#ff94a6" : "#ffe09a",
        fontStyle: "bold",
        stroke: "#02070c",
        strokeThickness: layout.primary ? 5 : 3
      }
    ).setOrigin(0.5).setDepth(14);
    if (this.reducedMotion) {
      this.time.delayedCall(180, () => text.destroy());
      return;
    }
    this.tweens.add({
      targets: text,
      y: text.y - (layout.primary ? 28 : 14),
      alpha: 0,
      scaleX: broken ? 0.94 : 1.12,
      scaleY: broken ? 0.94 : 1.12,
      duration: 620,
      ease: "Cubic.easeOut",
      onComplete: () => text.destroy()
    });
  }

  private spawnImpact(layout: BoardLayout, color: number, event: Record<string, unknown>): void {
    const view = this.view;
    if (!view) return;
    const eventCells = Array.isArray(event.cells) ? event.cells : [];
    const firstCell = rawRecord(eventCells[0] ?? rawRecord(event.cell));
    const row = typeof firstCell.row === "number" ? firstCell.row : null;
    const col = typeof firstCell.col === "number" ? firstCell.col : null;
    const logicalX = row !== null && col !== null
      ? view.logicalBoard.bubbleRadius + col * view.logicalBoard.bubbleRadius * 2
        + (row % 2 === 1 ? view.logicalBoard.bubbleRadius : 0)
      : view.logicalBoard.width / 2;
    const logicalY = row !== null
      ? view.logicalBoard.bubbleRadius + row * view.logicalBoard.hexRowHeight
      : view.logicalBoard.height * 0.4;
    const ring = this.add.graphics({
      x: layout.x + logicalX * layout.scale,
      y: layout.y + logicalY * layout.scale
    });
    ring.lineStyle(layout.primary ? 5 : 3, color, 0.9);
    ring.strokeCircle(0, 0, (layout.primary ? 28 : 18) * layout.scale);
    ring.fillStyle(color, 0.18);
    ring.fillCircle(0, 0, (layout.primary ? 20 : 12) * layout.scale);
    if (this.reducedMotion) {
      this.time.delayedCall(120, () => ring.destroy());
      return;
    }
    this.tweens.add({
      targets: ring,
      alpha: 0,
      scaleX: 1.9,
      scaleY: 1.9,
      duration: 420,
      ease: "Cubic.easeOut",
      onComplete: () => ring.destroy()
    });
  }

  private primaryBoard(): PuzzleBoardView | undefined {
    return this.view?.boards.find((board) => board.playerId === this.view?.primaryPlayerId) ?? this.view?.boards[0];
  }

  private primaryLayout(): BoardLayout | undefined {
    const playerId = this.view?.primaryPlayerId;
    return playerId ? this.layouts.get(playerId) : undefined;
  }

  private applyPixelArtTextureFilters(): void {
    [
      FOAM_POP_TEXTURE_KEYS.bubbles,
      FOAM_POP_TEXTURE_KEYS.mascot,
      FOAM_POP_TEXTURE_KEYS.mascotWheel,
      this.portrait ? FOAM_POP_TEXTURE_KEYS.backgroundPortrait : FOAM_POP_TEXTURE_KEYS.backgroundLandscape
    ].forEach((key) => {
      if (this.textures.exists(key)) this.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    });
  }

  private drawGeneratedBackground(): void {
    if (this.compact) return;
    const key = this.portrait
      ? FOAM_POP_TEXTURE_KEYS.backgroundPortrait
      : FOAM_POP_TEXTURE_KEYS.backgroundLandscape;
    if (!this.textures.exists(key)) return;
    this.generatedBackground = this.add.image(this.renderWidth / 2, this.renderHeight / 2, key)
      .setDisplaySize(this.renderWidth, this.renderHeight)
      .setDepth(-19);
  }

  private syncMascot(aim: number): void {
    if (this.compact) return;
    const wheelAvailable = this.textures.exists(FOAM_POP_TEXTURE_KEYS.mascotWheel);
    const fallbackAvailable = this.textures.exists(FOAM_POP_TEXTURE_KEYS.mascot);
    if (!wheelAvailable && !fallbackAvailable) return;
    const layout = this.primaryLayout();
    if (!layout) {
      this.mascot?.setVisible(false);
      return;
    }
    const textureKey = wheelAvailable ? FOAM_POP_TEXTURE_KEYS.mascotWheel : FOAM_POP_TEXTURE_KEYS.mascot;
    if (!this.mascot) {
      this.mascot = this.add.sprite(
        0,
        0,
        textureKey,
        wheelAvailable ? FOAM_POP_WHEEL_MASCOT_FRAME.center : FOAM_POP_MASCOT_FRAME.idle
      )
        .setOrigin(0.5, wheelAvailable ? 0.96875 : 0.78)
        .setDepth(4);
    } else if (this.mascot.texture.key !== textureKey) {
      this.mascot.setTexture(textureKey);
    }
    const launcherY = layout.y + (this.view?.logicalBoard.shooterY ?? PUZZLE_SHOOTER_Y) * layout.scale;
    const displaySize = this.portrait ? 92 * layout.scale : wheelAvailable
      ? Math.max(126, 148 * layout.scale)
      : Math.max(92, 112 * layout.scale);
    const matchWinner = this.view?.matchWinner;
    const statusFrame = wheelAvailable
      ? layout.board.status === "lost" || (matchWinner !== null && matchWinner !== layout.board.team)
        ? FOAM_POP_WHEEL_MASCOT_FRAME.windUp
        : matchWinner === layout.board.team
          ? FOAM_POP_WHEEL_MASCOT_FRAME.ready
          : null
      : layout.board.status === "cleared"
        ? FOAM_POP_MASCOT_FRAME.cheer
        : layout.board.status === "lost"
          ? FOAM_POP_MASCOT_FRAME.danger
          : null;
    const aimFrame = aim < -18
      ? FOAM_POP_MASCOT_FRAME.aimLeft
      : aim > 18
        ? FOAM_POP_MASCOT_FRAME.aimRight
        : FOAM_POP_MASCOT_FRAME.aimUp;
    const override = this.mascotOverrideFrame !== null && this.time.now < this.mascotOverrideUntil
      ? this.mascotOverrideFrame
      : null;
    const desiredFrame = statusFrame ?? override ?? (wheelAvailable ? foamPopWheelAimFrame(aim) : aimFrame);
    if (!this.mascot.anims.isPlaying) this.mascot.setFrame(desiredFrame);
    this.mascot
      .setPosition(layout.x + (this.portrait ? 49 : -69) * layout.scale, launcherY + 18 * layout.scale)
      .setDisplaySize(displaySize, displaySize)
      .setOrigin(0.5, wheelAvailable ? 0.96875 : 0.78)
      .setVisible(true);
  }

  private createMascotAnimations(): void {
    if (
      !this.textures.exists(FOAM_POP_TEXTURE_KEYS.mascotWheel)
      || this.anims.exists(FOAM_POP_WHEEL_FIRE_ANIMATION_KEY)
    ) return;
    this.anims.create({
      key: FOAM_POP_WHEEL_FIRE_ANIMATION_KEY,
      frames: [
        { key: FOAM_POP_TEXTURE_KEYS.mascotWheel, frame: FOAM_POP_WHEEL_MASCOT_FRAME.windUp },
        { key: FOAM_POP_TEXTURE_KEYS.mascotWheel, frame: FOAM_POP_WHEEL_MASCOT_FRAME.lever },
        { key: FOAM_POP_TEXTURE_KEYS.mascotWheel, frame: FOAM_POP_WHEEL_MASCOT_FRAME.fire },
        { key: FOAM_POP_TEXTURE_KEYS.mascotWheel, frame: FOAM_POP_WHEEL_MASCOT_FRAME.fire },
        { key: FOAM_POP_TEXTURE_KEYS.mascotWheel, frame: FOAM_POP_WHEEL_MASCOT_FRAME.recover }
      ],
      frameRate: 12,
      repeat: 0
    });
  }

  private receiveMascotEvent(type: string, event: Record<string, unknown>): void {
    if (this.compact || !this.view) return;
    const playerId = typeof event.playerId === "string" ? event.playerId : undefined;
    if (playerId && playerId !== this.view.primaryPlayerId) return;
    let frame: number | null = null;
    let duration = 0;
    const wheelAvailable = this.textures.exists(FOAM_POP_TEXTURE_KEYS.mascotWheel);
    if (type === "fired") {
      this.syncMascot(this.readAim());
      if (wheelAvailable && this.mascot) {
        if (this.reducedMotion) {
          this.mascotOverrideFrame = FOAM_POP_WHEEL_MASCOT_FRAME.fire;
          this.mascotOverrideUntil = this.time.now + 150;
          this.syncMascot(this.readAim());
        } else {
          this.mascotOverrideFrame = null;
          this.mascot.play(FOAM_POP_WHEEL_FIRE_ANIMATION_KEY, true);
        }
        return;
      }
      frame = FOAM_POP_MASCOT_FRAME.fire;
      duration = 190;
    } else if (type === "bubble_held") {
      frame = wheelAvailable ? FOAM_POP_WHEEL_MASCOT_FRAME.ready : FOAM_POP_MASCOT_FRAME.hold;
      duration = 360;
    } else if (type === "matched" || type === "dropped" || type === "special_triggered") {
      frame = wheelAvailable ? FOAM_POP_WHEEL_MASCOT_FRAME.ready : FOAM_POP_MASCOT_FRAME.cheer;
      duration = 430;
    } else if (type === "pressure_applied") {
      frame = wheelAvailable ? FOAM_POP_WHEEL_MASCOT_FRAME.windUp : FOAM_POP_MASCOT_FRAME.danger;
      duration = 420;
    }
    if (frame === null) return;
    this.mascotOverrideFrame = frame;
    this.mascotOverrideUntil = this.time.now + (this.reducedMotion ? Math.min(duration, 150) : duration);
    this.syncMascot(this.readAim());
  }
}
